import { useEffect } from 'react';
import { useThrottle } from '@wix/da-hooks/pkg/useThrottle';
import type { PrimaryPlacement, SecondaryPlacement } from '../types';

type PopoverOptions = {
  placement?: PrimaryPlacement;
  secondaryPlacement?: SecondaryPlacement;
  enabled?: boolean;
  updatePositionOnScroll?: boolean;
  throttleDelay?: number;
};

/** popper-free, very basic alignment for when all the fancy stuff is
 *  overkill and performance matters */
export function useBasicPopoverPositioning(
  contentNode: HTMLElement | null,
  popoverNode: HTMLElement | null,
  {
    placement = 'right',
    secondaryPlacement = 'middle',
    enabled = true,
    updatePositionOnScroll = true,
    throttleDelay = 100,
  }: PopoverOptions = {}
) {
  const positionTooltip = useThrottle(
    () => {
      if (!contentNode || !popoverNode) {
        return;
      }

      const contentRect = contentNode.getBoundingClientRect();
      const tooltipRect = popoverNode.getBoundingClientRect();
      const popoverEl = popoverNode;
      // Intentionally mutate DOM for positioning; popoverEl is the node to position
      /* eslint-disable-next-line react-compiler/react-compiler -- DOM style mutation required for placement */
      popoverEl.style.position = 'fixed';
      popoverEl.style.willChange = 'transform';

      // TODO: add support for other placements
      switch (placement) {
        case 'right':
        default:
          popoverEl.style.top = '0';
          popoverEl.style.left = `${contentRect.right}px`;
          switch (secondaryPlacement) {
            case 'start':
              popoverEl.style.transform = `translateY(${
                contentRect.y - contentRect.height / 2
              }px)`;
              break;
            case 'end':
              popoverEl.style.transform = `translateY(${
                contentRect.y + contentRect.height - tooltipRect.height
              }px)`;
              break;
            case 'middle':
            default:
              popoverEl.style.transform = `translateY(${
                contentRect.y + contentRect.height / 2 - tooltipRect.height / 2
              }px)`;
          }
      }
    },
    [contentNode, popoverNode, placement],
    throttleDelay,
    { leading: true, trailing: true }
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }

    positionTooltip();

    if (updatePositionOnScroll) {
      window.addEventListener('scroll', positionTooltip);

      return () => {
        window.removeEventListener('scroll', positionTooltip);
      };
    }
  }, [enabled, positionTooltip, updatePositionOnScroll]);

  return { positionTooltip };
}
